import { Checkbox } from '@/components/ui/checkbox';
import { CoverReportDetailsBody } from './CoverReportDetails';
import CoverageTimeSelect from './CoverageTimeSelect';
import CoverReportHistory from './CoverReportHistory';
import { uncoveredCoverIntervals } from '@/walkin/doctor-cover-gaps';
import { formatIstTime } from '@/lib/datetime-ist';
import { useEffect, useRef, useState } from 'react';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { portalApiGet, portalApiPost, PortalApiError } from '@/lib/portal-api';
import ModalShell from '@/dashboard/ui/ModalShell';
import AcButton from '@/dashboard/ui/AcButton';
import { AcSelect, FormField } from '@/dashboard/ui/FormControl';
import DoctorCoverResults from '@/walkin/DoctorCoverResults';
import type { CoverResults } from '@/walkin/DoctorCoverResults';
import { coverageInterval, formatCoverageDate, localCoverageTime } from './doctor-coverage-view';
import type { CoverageDay, CoverageReceptionReport } from './doctor-coverage-view';
import './arrange-doctor-cover.css';
export default function ArrangeDoctorCover({
  day,
  report,
  clinicName,
  onClose,
  onChanged,
}: {
  day: CoverageDay;
  report: CoverageReceptionReport;
  clinicName: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [data, setData] = useState<CoverResults | null>(null),
    [view, setView] = useState<'cover' | 'no_cover' | 'details' | 'results'>('cover');
  const [doctor, setDoctor] = useState(''),
    [second, setSecond] = useState(''),
    [also, setAlso] = useState(false),
    [method, setMethod] = useState(''),
    [from, setFrom] = useState(''),
    [until, setUntil] = useState(''),
    [checked, setChecked] = useState(false),
    [note, setNote] = useState('');
  const [absenceFrom, setAbsenceFrom] = useState(''),
    [absenceUntil, setAbsenceUntil] = useState('');
  const [busy, setBusy] = useState(false),
    [ready, setReady] = useState(false),
    [error, setError] = useState('');
  const epoch = useRef(0),
    saving = useRef(false),
    key = useRef<string | null>(null),
    pending = useRef<Record<string, unknown> | null>(null);
  const duty = day.doctors.find((d) => d.legacyDoctorId === report.doctorId)?.ownRole;
  const choices = day.doctors.filter((d) => d.legacyDoctorId !== report.doctorId);
  const alternatives = choices.filter((d) => String(d.legacyDoctorId) !== doctor);
  const unknown = !data?.report.starts_at || !data?.report.ends_at;
  async function read() {
    const token = epoch.current;
    try {
      const result = await portalApiGet<CoverResults>(
        `/reception/doctor-absence-reports/${report.reportId}/affected-bookings?clinicId=${day.clinicId}`,
      );
      if (token === epoch.current) setData(result);
      return result;
    } catch (e) {
      if (token === epoch.current) {
        setData(null);
        if (e instanceof PortalApiError && [401, 403].includes(e.status)) {
          setReady(false);
          pending.current = null;
          if (key.current) sessionStorage.removeItem(key.current);
          key.current = null;
        }
      }
      throw e;
    }
  }
  function keep() {
    if (!key.current) throw new Error('Draft unavailable');
    sessionStorage.setItem(
      key.current,
      JSON.stringify({
        doctor,
        second,
        also,
        method,
        from,
        until,
        absenceFrom,
        absenceUntil,
        checked,
        note,
        view,
        pending: pending.current,
      }),
    );
  }
  useEffect(() => {
    const token = ++epoch.current;
    const client = getSupabaseBrowserClient();
    let actor: string | null = null;
    setReady(false);
    setData(null);
    key.current = null;
    pending.current = null;
    void (async () => {
      actor = client ? ((await client.auth.getSession()).data.session?.user.id ?? null) : null;
      if (!actor) throw new Error('Session unavailable');
      const storage = `arrange-cover:v1:${actor}:${day.clinicId}:${report.reportId}`;
      const raw = sessionStorage.getItem(storage),
        saved = raw ? JSON.parse(raw) : null;
      const result = await read();
      if (token !== epoch.current) return;
      if (
        saved?.pending &&
        (saved.pending.clinicId !== day.clinicId || saved.pending.reportId !== report.reportId)
      )
        throw new Error('Draft scope changed');
      key.current = storage;
      if (saved) {
        setDoctor(saved.doctor ?? '');
        setSecond(saved.second ?? '');
        setAlso(saved.also === true || !!saved.second);
        setMethod(saved.method ?? '');
        setFrom(saved.from ?? '');
        setUntil(saved.until ?? '');
        setAbsenceFrom(
          saved.absenceFrom ??
            (saved.pending?.absenceHours?.startsAt
              ? localCoverageTime(saved.pending.absenceHours.startsAt, day.timeZone)
              : ''),
        );
        setAbsenceUntil(
          saved.absenceUntil ??
            (saved.pending?.absenceHours?.endsAt
              ? localCoverageTime(saved.pending.absenceHours.endsAt, day.timeZone)
              : ''),
        );
        setChecked(saved.checked === true);
        setNote(saved.note ?? '');
        pending.current = saved.pending ?? null;
        setView(
          pending.current && ['cover', 'no_cover'].includes(saved.view)
            ? saved.view
            : result.decision
              ? 'details'
              : 'cover',
        );
      } else {
        if (result.report.starts_at)
          setFrom(localCoverageTime(result.report.starts_at, day.timeZone));
        if (result.report.ends_at) setUntil(localCoverageTime(result.report.ends_at, day.timeZone));
        setView(result.decision ? 'details' : 'cover');
      }
      setReady(true);
    })().catch(() => {
      if (token === epoch.current)
        setError('Cover details could not be restored. Reopen after checking access.');
    });
    const sub = client?.auth.onAuthStateChange((_event, session) => {
      if (actor && session?.user.id !== actor) {
        epoch.current++;
        setData(null);
        setReady(false);
        pending.current = null;
        if (key.current) sessionStorage.removeItem(key.current);
        key.current = null;
        setError('Session changed. Reopen cover.');
      }
    });
    return () => {
      epoch.current++;
      sub?.data.subscription.unsubscribe();
    };
  }, [day.clinicId, report.reportId]);
  useEffect(() => {
    if (!ready) return;
    try {
      keep();
    } catch {
      setReady(false);
      setError('Draft could not be kept. Save is unavailable.');
    }
  }, [
    ready,
    doctor,
    second,
    also,
    method,
    from,
    until,
    absenceFrom,
    absenceUntil,
    checked,
    note,
    view,
    busy,
  ]);
  async function save(state: 'confirmed' | 'no_cover') {
    if (saving.current || !ready) return;
    const token = epoch.current;
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      if (!pending.current) {
        const ids = [doctor, ...(second ? [second] : [])];
        const interval =
          state === 'confirmed' ? coverageInterval(report.date, from, until, day.timeZone) : null;
        const absenceInterval =
          state === 'confirmed' && unknown
            ? coverageInterval(report.date, absenceFrom, absenceUntil, day.timeZone)
            : null;
        if (
          state === 'confirmed' &&
          (!duty ||
            !method ||
            (also && !second) ||
            new Set(ids).size !== ids.length ||
            (unknown && !checked))
        )
          throw new Error('Check the replacement, duty and absence hours.');
        pending.current = {
          clinicId: day.clinicId,
          reportId: report.reportId,
          requestId: crypto.randomUUID(),
          state,
          note,
          absenceHours:
            state === 'confirmed' && unknown ? { checked: true, ...absenceInterval } : null,
          doctors:
            state === 'confirmed'
              ? ids.map((id) => {
                  const d = choices.find((x) => String(x.legacyDoctorId) === id);
                  if (!d) throw new Error('Choose a current clinic doctor');
                  return {
                    personId: d.personId,
                    legacyDoctorId: d.legacyDoctorId,
                    coveringRole: duty,
                    ...interval,
                    confirmationMethod: method,
                  };
                })
              : [],
        };
      }
      keep();
      await portalApiPost('/coc/doctor-cover-decisions', pending.current);
      if (token !== epoch.current) return;
      const next = await read();
      if (token !== epoch.current) return;
      if (next.decision?.operationId !== pending.current.requestId)
        throw new Error('Saved decision readback not confirmed');
      pending.current = null;
      if (key.current) sessionStorage.removeItem(key.current);
      setView('details');
      onChanged();
    } catch (e) {
      if (token !== epoch.current) return;
      const status = e instanceof PortalApiError ? e.status : 0;
      if ([400, 401, 403, 409].includes(status)) pending.current = null;
      if ([401, 403].includes(status)) {
        setData(null);
        setReady(false);
      }
      setError(
        status === 409
          ? 'Cover changed or overlaps. Reopen the saved record.'
          : status === 403
            ? 'Cover access is unavailable.'
            : 'Save not confirmed. Your entries are kept. Retry.',
      );
    } finally {
      saving.current = false;
      if (token === epoch.current) setBusy(false);
    }
  }
  let draftInterval: { startsAt: string; endsAt: string } | null = null;
  try {
    if (from && until) draftInterval = coverageInterval(report.date, from, until, day.timeZone);
  } catch {
    /* Invalid entry remains disabled; no invented gap. */
  }
  let draftAbsence: { startsAt: string; endsAt: string } | null = null;
  try {
    if (unknown && absenceFrom && absenceUntil)
      draftAbsence = coverageInterval(report.date, absenceFrom, absenceUntil, day.timeZone);
  } catch {
    /* Missing hours remain unavailable until confirmed. */
  }
  const gapRows = data
    ? uncoveredCoverIntervals(
        (view === 'cover' && unknown ? draftAbsence?.startsAt : undefined) ??
          data.decision?.absenceHours?.startsAt ??
          data.report.starts_at,
        (view === 'cover' && unknown ? draftAbsence?.endsAt : undefined) ??
          data.decision?.absenceHours?.endsAt ??
          data.report.ends_at,
        view === 'cover'
          ? draftInterval
            ? [
                {
                  status: 'confirmed',
                  starts_at: draftInterval.startsAt,
                  ends_at: draftInterval.endsAt,
                },
              ]
            : []
          : data.coordination,
      )
    : null;
  const gapNotice =
    gapRows && gapRows.length > 0 ? (
      <div className="arrange-cover__gap">
        <strong>
          {gapRows
            .map(
              ([a, b]) =>
                `${formatIstTime(new Date(a).toISOString())}–${formatIstTime(new Date(b).toISOString())}`,
            )
            .join(' · ')}{' '}
          uncovered
        </strong>
        <small>COC will arrange cover or agree a new time.</small>
      </div>
    ) : null;
  const locked = !ready || busy || pending.current !== null;
  if (view === 'results')
    return (
      <DoctorCoverResults
        clinicId={day.clinicId}
        clinicName={clinicName}
        reportId={report.reportId}
        role="coc"
        doctors={day.doctors.map((d) => ({ id: d.legacyDoctorId, name: d.name }))}
        onClose={() => setView('details')}
        onChanged={onChanged}
      />
    );
  return (
    <ModalShell
      testId="arrange-doctor-cover"
      title={
        view === 'details'
          ? 'Doctor cover'
          : view === 'no_cover'
            ? 'No cover found'
            : 'Arrange cover'
      }
      eyebrow={`${clinicName} · COC`}
      trapFocus
      onClose={onClose}
      closeDisabled={busy || pending.current !== null}
      footer={
        <>
          {pending.current ? (
            <AcButton
              variant="primary"
              disabled={busy || !ready}
              onClick={() => void save(pending.current!.state as 'confirmed' | 'no_cover')}
            >
              Retry saved request
            </AcButton>
          ) : view === 'cover' ? (
            <>
              <AcButton disabled={locked} onClick={() => setView('no_cover')}>
                No cover found
              </AcButton>
              <AcButton
                variant="primary"
                disabled={
                  locked ||
                  !duty ||
                  !doctor ||
                  (also && !second) ||
                  !method ||
                  !from ||
                  !until ||
                  from >= until ||
                  (unknown && (!checked || !draftAbsence))
                }
                onClick={() => void save('confirmed')}
              >
                Confirm cover
              </AcButton>
            </>
          ) : view === 'no_cover' ? (
            <>
              <AcButton disabled={busy} onClick={() => setView('cover')}>
                Back
              </AcButton>
              <AcButton variant="primary" disabled={locked} onClick={() => void save('no_cover')}>
                Save · COC to rebook
              </AcButton>
            </>
          ) : (
            <AcButton onClick={onClose}>Done</AcButton>
          )}
        </>
      }
    >
      <div className="arrange-cover" data-cover-view={view}>
        {error && <p role="alert">{error}</p>}
        {!data ? (
          <p role="status">{error ? 'Cover unavailable.' : 'Loading cover…'}</p>
        ) : (
          <>
            {view !== 'details' && (
              <div className="arrange-cover__context">
                <span className="arrange-cover__initial" aria-hidden="true">
                  {report.doctorName
                    .replace(/^Dr\.?\s*/i, '')
                    .slice(0, 1)
                    .toUpperCase()}
                </span>
                <div>
                  <strong>
                    {report.doctorName} {view === 'cover' ? 'is unavailable' : ''}
                  </strong>
                  <small>
                    {formatCoverageDate(report.date)} ·{' '}
                    {unknown
                      ? 'Hours need confirmation'
                      : `${localCoverageTime(data.report.starts_at!, day.timeZone)}–${localCoverageTime(data.report.ends_at!, day.timeZone)} IST`}
                  </small>
                </div>
              </div>
            )}
            {view === 'cover' ? (
              <>
                <fieldset disabled={locked}>
                  <FormField id="hybrid-cover-doctor" label="Cover doctor">
                    <AcSelect
                      id="hybrid-cover-doctor"
                      value={doctor}
                      onChange={(e) => {
                        const next = e.target.value;
                        setDoctor(next);
                        const others = choices.filter((d) => String(d.legacyDoctorId) !== next);
                        if (also && others.length === 1)
                          setSecond(String(others[0].legacyDoctorId));
                        else if (second === next) setSecond('');
                      }}
                    >
                      <option value="">Choose doctor</option>
                      {choices.map((d) => (
                        <option key={d.legacyDoctorId} value={d.legacyDoctorId}>
                          {d.name}
                        </option>
                      ))}
                    </AcSelect>
                  </FormField>
                  <label className="arrange-cover__check">
                    <Checkbox
                      checked={also}
                      disabled={!doctor || alternatives.length === 0}
                      onCheckedChange={(checked) => {
                        setAlso(checked);
                        setSecond(
                          checked && alternatives.length === 1
                            ? String(alternatives[0].legacyDoctorId)
                            : '',
                        );
                      }}
                    />
                    <span>
                      {alternatives.length === 1 ? `Also ${alternatives[0].name}` : 'Also covering'}
                      <small>Share eligible bookings by round robin</small>
                    </span>
                  </label>
                  {also && alternatives.length > 1 && (
                    <FormField id="hybrid-cover-second" label="Also covering (optional)">
                      <AcSelect
                        id="hybrid-cover-second"
                        value={second}
                        onChange={(e) => setSecond(e.target.value)}
                      >
                        <option value="">Choose doctor</option>
                        {alternatives.map((d) => (
                          <option key={d.legacyDoctorId} value={d.legacyDoctorId}>
                            {d.name}
                          </option>
                        ))}
                      </AcSelect>
                    </FormField>
                  )}

                  {unknown && (
                    <section aria-label="Confirmed absence hours">
                      <h3>Absence hours · confirmed with {report.doctorName}</h3>
                      <div className="doctor-coverage__times">
                        <FormField id="hybrid-absence-from" label="Absent from · IST">
                          <CoverageTimeSelect
                            id="hybrid-absence-from"
                            value={absenceFrom}
                            onChange={setAbsenceFrom}
                          />
                        </FormField>
                        <FormField id="hybrid-absence-until" label="Absent until · IST">
                          <CoverageTimeSelect
                            id="hybrid-absence-until"
                            value={absenceUntil}
                            onChange={setAbsenceUntil}
                          />
                        </FormField>
                      </div>
                    </section>
                  )}
                  <div className="doctor-coverage__times">
                    <FormField id="hybrid-cover-from" label="From · IST">
                      <CoverageTimeSelect id="hybrid-cover-from" value={from} onChange={setFrom} />
                    </FormField>
                    <FormField id="hybrid-cover-until" label="Until · IST">
                      <CoverageTimeSelect
                        id="hybrid-cover-until"
                        value={until}
                        onChange={setUntil}
                      />
                    </FormField>
                  </div>
                  {gapNotice}
                  <FormField
                    id="hybrid-cover-method"
                    label={second ? 'Both doctors confirmed by' : 'Confirmed with doctor by'}
                  >
                    <AcSelect
                      id="hybrid-cover-method"
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                    >
                      <option value="">Choose method</option>
                      <option value="phone">Phone</option>
                      <option value="message">WhatsApp</option>
                      <option value="in_person">In person</option>
                    </AcSelect>
                  </FormField>
                  {unknown && (
                    <label className="arrange-cover__check">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(checked) => setChecked(checked)}
                      />
                      I checked the absence date and hours with {report.doctorName}
                    </label>
                  )}
                </fieldset>
                {!duty && (
                  <p role="alert">
                    The absent doctor’s covering duty is not recorded. Confirm the existing roster
                    before arranging cover.
                  </p>
                )}
                <div className="arrange-cover__notice">
                  <strong>Eligible bookings move when you confirm.</strong>
                  <p>Same time. Auto-assigned only. Other bookings stay for review.</p>
                </div>
                <details>
                  <summary>Original report &amp; rules</summary>
                  <p>{report.detail}</p>
                  <p>Weekly schedule stays unchanged. No automatic patient messages.</p>
                </details>
              </>
            ) : view === 'no_cover' ? (
              <>
                <p>
                  <strong>COC will arrange new appointments.</strong>
                </p>
                <p>
                  Reception tells patients already at the clinic. COC contacts the rest. Bookings
                  stay unchanged until a new time is agreed.
                </p>
                <FormField id="hybrid-cover-note" label="Note (optional)">
                  <textarea
                    className="ac-input"
                    rows={2}
                    id="hybrid-cover-note"
                    disabled={locked}
                    maxLength={500}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="What has been tried?"
                  />
                </FormField>
              </>
            ) : (
              <>
                <CoverReportDetailsBody
                  data={data}
                  doctors={day.doctors.map((d) => ({ id: d.legacyDoctorId, name: d.name }))}
                />
                <AcButton onClick={() => setView('results')}>Review patient follow-up</AcButton>
                <CoverReportHistory
                  clinicId={day.clinicId}
                  reportId={report.reportId}
                  detail={report.detail}
                />
              </>
            )}
          </>
        )}
      </div>
    </ModalShell>
  );
}
